import React from "react";
import {Typography} from "@mui/material";
import Carousel, {type DotProps, type ResponsiveType} from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import cx from "classnames";

import {COLORS, EMOJIS} from "../../../../../config/constants";
import TextCard from "../../components/text-card/TextCard";
import H1 from "../../components/typography/H1";
import type {TextCardData} from "../../types";
import {useWindowWidth} from "../../../../../services/useWindowWidth";

import styles from './ConnectorCarouselSection.module.scss';
import {translatePartnerText} from '../../locales/language';

const cards: TextCardData[] = [
    {
        emoji: EMOJIS.DIRECT_HIT,
        title: 'Vedmarket brings you qualified leads',
        text: 'Receive verified requests from importers with a budget already in place. Grow your client base without expanding your sales team'
    },
    {
        emoji: '🚀',
        title: 'Vedmarket makes client service efficient',
        text: 'Work with existing clients by offering a complete import cost calculation that includes your services'
    },
    {
        emoji: EMOJIS.GEAR,
        title: 'Vedmarket equips you for business',
        text: 'Use a full set of tools to deliver quality service as a single provider of logistics and related services'
    }
];

const responsive: ResponsiveType = {
    desktop: {
        breakpoint: {max: 3000, min: 1400},
        items: 1,
        partialVisibilityGutter: 392,
    },
    laptop: {
        breakpoint: {max: 1399, min: 1200},
        items: 1,
        partialVisibilityGutter: 192,
    },
    medium: {
        breakpoint: {max: 991, min: 768},
        items: 1,
        partialVisibilityGutter: 24,
    },
    small: {
        breakpoint: {max: 767, min: 576},
        items: 1,
        partialVisibilityGutter: 16,
    },
    tablet: {
        breakpoint: {max: 1199, min: 992},
        items: 1,
        partialVisibilityGutter: 246,
    },
    mobile: {
        breakpoint: {max: 575, min: 0},
        items: 1,
        partialVisibilityGutter: 16,
    },
};

function getDesktopPartialVisibilityGutter(viewportWidth: number): number {
    if (viewportWidth >= 1400) {
        return 228 + (viewportWidth - 1400) / 2;
    }

    return Math.max(0, viewportWidth - 1172);
}

function getLastSlideTransform(viewportWidth: number): number {
    if (viewportWidth >= 1400) {
        return -((viewportWidth - 1400) / 2);
    }

    if (viewportWidth >= 1200) {
        return 0;
    }

    if (viewportWidth >= 768) {
        return -24;
    }

    return -16;
}

function getCarouselResponsive(viewportWidth: number): ResponsiveType {
    const desktopPartialVisibilityGutter = getDesktopPartialVisibilityGutter(viewportWidth);

    return {
        ...responsive,
        desktop: {
            ...responsive.desktop,
            partialVisibilityGutter: desktopPartialVisibilityGutter,
        },
        laptop: {
            ...responsive.laptop,
            partialVisibilityGutter: desktopPartialVisibilityGutter,
        },
    };
}

function CardConnector() {
    return (
        <div className={styles.connector}>
            <Typography
                sx={{
                    lineHeight: 1,
                    letterSpacing: '0',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: '700',
                    '@media screen and (min-width: 1200px)': {
                        fontSize: '48px',
                    },
                    '@media (min-width: 992px) and (max-width: 1399px)': {
                        fontSize: '36px',
                    },
                    '@media (min-width: 768px) and (max-width: 1399px)': {
                        fontSize: '24px',
                    },
                    '@media screen and (max-width: 767px)': {
                        fontSize: '24px',
                    },
                }}>
                +
            </Typography>
        </div>
    );
}

function CarouselDot({active, index, onClick}: DotProps) {
    return (
        <button
            aria-current={active ? 'true' : undefined}
            aria-label={translatePartnerText('Show card {{number}}', {number: (index ?? 0) + 1})}
            className={`${styles.paginationDot} ${active ? styles.paginationActive : ''}`}
            onClick={onClick}
            type="button"
        />
    );
}

function ConnectorCarouselSection() {
    const [activeSlide, setActiveSlide] = React.useState(0);
    const width = useWindowWidth();
    const isLargeScreen = width >= 992;
    const carouselResponsive = getCarouselResponsive(width);
    const renderDotsOutside = width >= 768 && width <= 991;
    const isLastSlide = activeSlide === cards.length - 1;
    const lastSlideTransform = isLastSlide ? getLastSlideTransform(width) : 0;

    return (
        <div className={styles.section} id="cooperation">
            <div className={styles.sectionContainer}>
                <div className={styles.title}>
                    <H1 sx={{
                        color: COLORS.WHITE,
                    }}>Flexible ways to partner</H1>
                </div>
                <div className={styles.staticViewport}>
                    {cards.flatMap((idea, index) => [
                        <TextCard key={`card-${index}`} card={idea}/>,
                        ...(index < cards.length - 1 ? [
                            <CardConnector key={`connector-${index}`}/>,
                        ] : []),
                    ])}
                </div>
                {!isLargeScreen && <Carousel
                    arrows={false}
                    containerClass={cx(styles.carouselViewport, {
                        [styles.lastSlideViewport]: isLastSlide,
                    })}
                    customDot={<CarouselDot/>}
                    customTransition="transform 300ms ease"
                    dotListClass={styles.pagination}
                    draggable
                    itemClass={styles.carouselItem}
                    keyBoardControl
                    minimumTouchDrag={40}
                    additionalTransfrom={lastSlideTransform}
                    beforeChange={(nextSlide) => setActiveSlide(nextSlide)}
                    partialVisible
                    responsive={carouselResponsive}
                    renderDotsOutside={renderDotsOutside}
                    showDots
                    sliderClass={styles.carouselTrack}
                    swipeable
                    transitionDuration={300}
                >
                    {cards.map((idea, index) => (
                        <TextCard
                            card={idea}
                            className={cx(styles.carouselCard, styles.carouselCardTransparent, {
                                [styles.activeCarouselCard]: index === activeSlide,
                            })}
                            key={index}
                            singleLineTitle={width >= 1200}
                        />
                    ))}
                </Carousel>}
            </div>
        </div>
    );
}

export default ConnectorCarouselSection;
